import Vue from 'vue';
import { AgGridVue } from 'ag-grid-vue';
import { debounce, escape, isUndefined } from 'lodash';

import AnimatedLogoLoader from '@/components/Loader/AnimatedLogoLoader.vue';
import { guardCsvExport } from '@/services/Grid/CsvFormulaGuard';

const FILTER_SET = 'SET';
const FILTER_TEXT = 'TEXT';

export default {
  components: {
    AgGridVue,
  },
  data() {
    return {
      modelId: '',
      loading: false,
      refreshing: false,
      resetting: false,
      rowData: [],
      gridWidth: 100,
      rowHeight: 47.5,
      gridDefaults: {},
      gridOptions: {},
      gridApi: null,
      columnApi: null,
      rowSelection: 'multiple',
      floatingFilter: false,
      cacheQuickFilter: true,
      suppressExcelExport: true,
      sideBarDef: {
        toolPanels: ['columns', 'filters'],
      },
      selected: {
        nodeIds: [],
      },
      query: {
        search: '',
      },
    };
  },
  computed: {
    count() {
      if (!this.gridApi) {
        return '';
      }

      return this.gridApi.paginationGetRowCount();
    },
    selectedIds() {
      return this.selected.nodeIds;
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    isAllRowsSelected() {
      return this.selectedCount === this.count;
    },
  },
  watch: {
    // Whenever this is updated, trigger the grid's search
    // Throttle to every 500ms so it doesn't lag
    'query.search': debounce(function (search) {
      this.gridApi.setQuickFilter(search);
    }, 500),

    refreshing(newRefresh) {
      if (!this.gridApi) {
        return;
      }

      if (newRefresh) {
        this.gridApi.showLoadingOverlay();
        return;
      }

      this.gridApi.hideOverlay();
    },
  },
  beforeMount() {
    this.gridOptions = {
      frameworkComponents: {
        AnimatedLogoLoader: Vue.extend(AnimatedLogoLoader),
      },
      loadingOverlayComponent: 'AnimatedLogoLoader',
      processSecondaryColGroupDef: (colGroupDef) => {
        colGroupDef.headerName = escape(colGroupDef.headerName);
      },
    };
  },
  methods: {
    /**
     * Gets called when the grid is ready
     */
    async onGridReady(params) {
      this.gridApi = params.api;
      guardCsvExport(this.gridApi);
      this.columnApi = params.columnApi;

      await this.setGridColumns();
      this.setDefaultGridOptions();
      await this.awaitGridOptionsLoaded();
      this.postGridReady(params);
    },

    /**
     * Wait for the per-client grid options fetch (dispatched on
     * FETCH_USER_SUCCESS) to settle before any grid first reads from the
     * store. Prevents a previous client's persisted config from being
     * applied while the new client's fetch is still in flight.
     * fetchGridOptions sets `loaded` true on both success and failure, so
     * this resolves either way.
     */
    awaitGridOptionsLoaded() {
      if (this.$store.getters.gridOptionsLoaded) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        const unwatch = this.$store.watch(
          (s, getters) => getters.gridOptionsLoaded,
          (loaded) => {
            if (loaded) {
              unwatch();
              resolve();
            }
          },
        );
      });
    },

    /**
     * Generic button visibility updater
     */
    updateButtonVisibility: debounce(function (buttonProperty, permissionCheckFn) {
      if (!this.gridApi || !this.gridReady) {
        return;
      }

      const selectedCount = this.gridApi.getSelectedNodes().length;

      if (selectedCount === 0) {
        this[buttonProperty] = false;
      } else {
        const selectedNodes = this.gridApi.getSelectedNodes();
        this[buttonProperty] = permissionCheckFn.call(this, selectedNodes);
      }

      this.$forceUpdate();
    }, 1),

    /**
     * Generic selection change handler
     */
    handleSelectionChanged(customLogicFn = null) {
      const selectedCount = this.gridApi.getSelectedNodes().length;

      if (customLogicFn && typeof customLogicFn === 'function') {
        customLogicFn.call(this, selectedCount);
      }

      this.$nextTick(() => {
        this.updateAssignButtonVisibility && this.updateAssignButtonVisibility();
      });
    },

    postGridReady(params) {},

    async setGridColumns() {},
    /**
     * Sets the grid up with:
     * Column state
     * Sort
     * Filters
     */
    async setGridOptions() {
      if (!this.gridName) {
        return true;
      }

      const name = this.gridName;

      let gridState = this.$store.getters.gridState(name);
      let gridSort = this.$store.getters.gridSort(name);
      let gridFilter = this.$store.getters.gridFilter(name);

      if (!gridState) {
        gridState = this.gridDefaults.state;
      }

      if (!gridSort) {
        gridSort = this.gridDefaults.sort;
      }

      if (!gridFilter) {
        gridFilter = this.gridDefaults.filter;
      }

      this.columnApi.setColumnState(gridState);

      this.gridApi.setSortModel(gridSort);
      this.gridApi.setFilterModel(gridFilter);
      this.gridApi.onFilterChanged();

      return true;
    },

    /**
     * Only allow to save the grid if it is not refreshing data
     * */
    saveGridOptions() {
      if (this.refreshing) {
        return false;
      }

      return this.saveOptions();
    },

    /**
     * Save the grid options (only allowed once per second)
     */
    saveOptions: debounce(function () {
      if (!this.gridName) {
        return true;
      }

      const name = this.gridName;
      const state = this.columnApi.getColumnState();
      const sort = this.gridApi.getSortModel();
      const filter = this.gridApi.getFilterModel();

      this.$store.dispatch('saveGridOptions', {
        name,
        state,
        sort,
        filter,
      });
    }, 1000),

    /**
     * Gets the currently selected rows
     */
    getSelectedNodes() {
      const selectedNodes = [];
      this.gridApi.getSelectedNodes().forEach((node) => {
        if (node.data) {
          selectedNodes.push(node);
        }
      });

      return selectedNodes;
    },

    /**
     * Selects all rows on grid
     */
    selectAll() {
      this.gridApi.selectAll();
    },

    /**
     * Deselects all rows on grid
     */
    deselectAll() {
      if (!this.gridApi) {
        return;
      }
      this.gridApi.deselectAll();
    },

    /**
     * Gets called when the user selects a row
     * We debounce it - If we do a select all, it will run this for *every* selected node.
     * Debounce helps us :D!
     */
    updateRowSelected: debounce(function () {
      this.selected.nodeIds = this.getSelectedNodes().map(node => node.data[this.modelId]);
    }, 1),

    /**
     * Sets original and default grid options
     */
    setDefaultGridOptions() {
      this.gridDefaults = {
        state: this.columnApi.getColumnState(),
        sort: this.gridApi.getSortModel(),
        filter: this.gridApi.getFilterModel(),
      };
    },

    /**
     * To be overridden
     */
    preResetGridToDefaultOptions() {},

    /**
     * Resets grid to absolute default
     */
    resetGridToDefaultOptions() {
      this.preResetGridToDefaultOptions();

      this.columnApi.setPivotMode(false);
      this.columnApi.setRowGroupColumns([]);
      this.gridApi.setSortModel(this.gridDefaults.sort);
      this.gridApi.setFilterModel(this.gridDefaults.filter);
      this.gridApi.setQuickFilter(null);
      this.query.search = '';

      this.gridApi.onFilterChanged();

      this.postResetGridToDefaultOptions();
    },

    /**
     * To be overridden
     */
    postResetGridToDefaultOptions() {},


    /**
     * To be overridden
     */
    preResetGridFilters() {},

    /**
     * Resets grid filters
     */
    resetGridFilters() {
      this.preResetGridFilters();
      this.gridApi.setSortModel(this.gridDefaults.sort);
      this.gridApi.setFilterModel(this.gridDefaults.filter);
      this.gridApi.onFilterChanged();
      this.postResetGridFilters();
    },

    /**
     * To be overridden
     */
    postResetGridFilters() {},

    /**
     * Helper to set the grid filter
     */
    changeGridFilter({
      field, value, type = FILTER_SET, hide = false, toggle = false,
    }) {
      if (hide) {
        field = `hide${field}`;
      }

      this.onFilterChange({
        field,
        value,
        type,
        toggle,
      });
    },

    /**
     * Gets called whenever a filter is changed
     * Can also be used to set a filter on AG Grid
     */
    onFilterChange(filter) {
      // Do not do while resetting
      if (this.resetting) {
        return;
      }

      if (filter.type === FILTER_SET) {
        this.filterSet(filter);
      } else if (filter.type === FILTER_TEXT) {
        this.filterText(filter);
      }

      this.gridApi.onFilterChanged();
    },

    /**
     * Set AG Grid filter for set type
     */
    filterSet({ field, value, toggle }) {
      let hide = false;

      // If the field name includes 'hide' in the string
      // It means we actually want to hide the value
      if (field.includes('hide')) {
        const stringParts = field.split('hide');
        hide = true;
        field = stringParts[1];
        field = field.charAt(0).toLowerCase() + field.substring(1);
      }

      // Get the filter instance of grid api
      const filterInstance = this.gridApi.getFilterInstance(field);

      // No value passed, select everything.
      if (isUndefined(value)) {
        filterInstance.selectEverything();
        return;
      }

      // If toggle is set, only set one value
      if (toggle) {
        filterInstance.selectNothing();
        filterInstance.selectValue(value);
        return;
      }

      // Only accepted arrays
      if (!Array.isArray(value)) {
        value = [value];
      }

      if (value.length === 0) {
        // No values, set everything
        filterInstance.selectEverything();
        filterInstance.setModel(null);
        return;
      }

      // If a value is set
      // Deselect the values
      if (hide) {
        value.forEach((v) => {
          // Empty values may be set as null on the grid so make sure we unset these too
          if (v === '') {
            filterInstance.unselectValue(null);
          }
          filterInstance.unselectValue(v);
        });
        return;
      }

      // Otherwise we have a group of values to set as the model
      filterInstance.setModel(value);
    },

    /**
     * Set AG Grid filter for text type
     */
    filterText({ field, value }) {
      const filterInstance = this.gridApi.getFilterInstance(field);

      let valueModel = null;
      if (value.length > 0) {
        valueModel = {};

        value.forEach((name, index) => {
          const condition = {
            filter: name,
            type: 'contains',
          };
          if (value.length > 1) {
            valueModel[`condition${index + 1}`] = condition;
            valueModel.operator = 'AND';
          } else {
            valueModel = condition;
          }
        });
      }

      filterInstance.setModel(valueModel);
    },

    onCellClicked() {},

    // Set the right click menu
    // https://www.ag-grid.com/javascript-grid-context-menu/
    getContextMenuItems() {
      return [
        'copy',
        'copyWithHeaders',
      ];
    },

    /**
     * Get the count of rows that should be exported
     */
    getExportRowCount() {
      const selectedNodes = this.getSelectedNodes();

      if (selectedNodes.length > 0) {
        return selectedNodes.length;
      }

      let count = 0;
      this.gridApi.forEachNodeAfterFilter(() => {
        count++;
      });

      return count;
    },

    /**
     * Common CSV export functionality with row limit check and loading overlay
     * @param {Function} processCellCallback - Function to process cell values
     * @param {number} rowLimit - Maximum number of rows allowed (default: 20000)
     */
    async exportToCsv(processCellCallback, rowLimit = 20000) {
      const rowCount = this.getExportRowCount();

      if (rowCount > rowLimit) {
        this.$notify({
          title: 'Too many rows for CSV export',
          text: `You are trying to export ${rowCount.toLocaleString()} rows. CSV export is limited to ${rowLimit.toLocaleString()} rows. Please use the "Download All" option instead for large datasets.`,
          type: 'error',
          duration: 8000,
        });
        return;
      }

      this.showLoadingOverlay = true;
      await this.$nextTick();

      // Small delay to ensure overlay is visible before heavy processing
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const onlySelected = this.getSelectedNodes().length > 0;
        const params = {
          processCellCallback,
          onlySelected,
        };

        this.gridApi.exportDataAsCsv(params);

        this.$notify({
          title: 'Export completed',
          text: 'CSV file has been downloaded successfully.',
          type: 'success',
          duration: 3000,
        });
      } catch (error) {
        console.error('CSV export failed:', error);
        this.$notify({
          title: 'Export failed',
          text: 'Unable to export CSV. Please try again.',
          type: 'error',
          duration: 5000,
        });
      } finally {
        this.showLoadingOverlay = false;
      }
    },
  },
};
